/**
 * Section A2 Page
 * A.2.活動類型(A6.4)說明
 */

import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { validateSession } from '../utils/sessionValidator';
import { usePermission } from '../hooks/usePermission';
import { useProject } from '../contexts/ProjectContext';
import sectionA2Service from '../services/sectionA2Service';
import { SectionA2 } from '../types/section_a2';
import RichTextEditor from '../components/RichTextEditor';
import FunctionPageHeader from '../components/FunctionPageHeader';
import { useToast } from '../contexts/ToastContext';
import '../styles/DataTable.css';

const SectionA2Page: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { hasPermission, loading: permissionLoading } = usePermission();
  const { selectedProjectId } = useProject();
  const toast = useToast();
  const hasInitialized = useRef(false);

  // ========== 資料狀態 ==========
  const [sectionA2Data, setSectionA2Data] = useState<SectionA2 | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // ========== 表單狀態 ==========
  const [formData, setFormData] = useState({
    project_active_type: ''
  });

  // ========== 初始化 ==========
  useEffect(() => {
    if (!hasInitialized.current && !permissionLoading) {
      validateSession('SectionA2Page');
      hasInitialized.current = true;
    }
  }, [permissionLoading]);

  // ========== 專案變更時載入資料 ==========
  useEffect(() => {
    if (!selectedProjectId) {
      setSectionA2Data(null);
      setFormData({ project_active_type: '' });
      return;
    }

    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);
        setSuccess(null);
        const data = await sectionA2Service.getSectionA2ByProject(selectedProjectId);
        if (data) {
          setSectionA2Data(data);
          setFormData({ project_active_type: data.project_active_type || '' });
        } else {
          setSectionA2Data(null);
          setFormData({ project_active_type: '' });
        }
      } catch (err: any) {
        console.error('載入 A.2 資料失敗:', err);
        setError(err.response?.data?.detail || '載入資料失敗');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [selectedProjectId]);

  // ========== 儲存 ==========
  const handleSave = async () => {
    if (!selectedProjectId) {
      setError(i18n.language === 'zh-TW' ? '請先在上方選擇專案' : 'Please select a project first');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      const updatedData = await sectionA2Service.updateSectionA2(selectedProjectId, {
        project_active_type: formData.project_active_type || undefined
      });

      setSectionA2Data(updatedData);
      setSuccess(i18n.language === 'zh-TW' ? '儲存成功' : 'Saved successfully');
      toast.success(i18n.language === 'zh-TW' ? '儲存成功' : 'Saved successfully');

    } catch (err: any) {
      console.error('儲存失敗:', err);
      const errorMsg = err.response?.data?.detail || '儲存失敗';
      setError(errorMsg);
      toast.error(i18n.language === 'zh-TW' ? `儲存失敗: ${errorMsg}` : `Save failed: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  // ========== 權限檢查 ==========
  if (permissionLoading) {
    return <div className="page-container"><div className="loading">{t('common.loading')}</div></div>;
  }

  if (!hasPermission('section_a2', 'read')) {
    return (
      <div className="page-container">
        <div className="error-message">{t('common.noPermission')}</div>
      </div>
    );
  }

  const canEdit = hasPermission('section_a2', 'update');

  // ========== 渲染 ==========
  return (
    <div className="page-container">
      <div className="page-header">
        <FunctionPageHeader funcCode="section_a2" />
      </div>

      {error && <div className="error-message" style={{ marginBottom: '16px' }}>{error}</div>}
      {success && <div className="success-message" style={{ marginBottom: '16px' }}>{success}</div>}

      {selectedProjectId && (
        <div style={{ marginBottom: '24px' }}>
          {loading ? (
            <div className="loading">{t('common.loading')}</div>
          ) : (
            <>
              <div style={{ marginBottom: '24px' }}>
                <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600 }}>
                  {i18n.language === 'zh-TW' ? '專案活動類型說明' : 'Project Activity Type Description'}
                </label>
                <RichTextEditor
                  value={formData.project_active_type}
                  onChange={(html) => setFormData({ ...formData, project_active_type: html })}
                  placeholder={i18n.language === 'zh-TW' ? '請輸入專案活動類型說明...' : 'Please enter project activity type description...'}
                  disabled={!canEdit}
                />
              </div>

              {canEdit && (
                <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                  <button className="btn-primary" onClick={handleSave} disabled={loading}>
                    {loading ? (i18n.language === 'zh-TW' ? '儲存中...' : 'Saving...') : (i18n.language === 'zh-TW' ? '儲存' : 'Save')}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {!selectedProjectId && !loading && (
        <div style={{ padding: '48px', textAlign: 'center', color: '#999', fontSize: '16px' }}>
          {i18n.language === 'zh-TW' ? '請先在上方選擇專案' : 'Please select a project from the top bar'}
        </div>
      )}
    </div>
  );
};

export default SectionA2Page;
